import { useState, useCallback, useEffect, useRef } from 'react'
import { BlockStack, Card, Text, TextField, Icon, Button, InlineStack, Divider, Link, Popover, ActionList } from '@shopify/polaris'
import { SearchIcon, MenuHorizontalIcon } from '@shopify/polaris-icons'
import { searchCustomers, type CustomerSearchResult, type CompanyLocation } from '../lib/graphql'
import { EditContactModal } from './EditContactModal'
import { EditAddressModal } from './EditAddressModal'
import { adminUrl } from '../lib/admin-url'

type PickerState = 'search' | 'submenu'

type CustomerSelection = {
    customer: CustomerSearchResult
    isB2B: boolean
    location: CompanyLocation | null
}

export type AddressOverrides = {
    contact: { email: string; phone: string } | null
    shipping: any | null
    billing: any | null
}

type Props = {
    selection: CustomerSelection | null
    onSelect: (selection: CustomerSelection) => void
    onRemove: () => void
    onAddressChange?: (overrides: AddressOverrides) => void
}

function AddressBlock({ label, address }: { label: string; address: any }) {
    if (!address) return null
    const lines = [
        [address.firstName, address.lastName].filter(Boolean).join(' '),
        address.companyName || address.company || '',
        address.address1,
        address.address2,
        [address.zip, address.city].filter(Boolean).join(' '),
        address.countryCode === 'DE' ? 'Germany' : address.countryCode,
    ].filter(Boolean)

    return (
        <BlockStack gap="100">
            <Text as="p" variant="headingSm" fontWeight="bold">{label}</Text>
            {lines.map((line, i) => (
                <Text key={i} as="p" tone="subdued" variant="bodySm">{line}</Text>
            ))}
            {address.phone && <Text as="p" tone="subdued" variant="bodySm">{address.phone}</Text>}
        </BlockStack>
    )
}

function SelectedCustomerView({ selection, onRemove, onAddressChange }: { selection: CustomerSelection; onRemove: () => void; onAddressChange?: (o: AddressOverrides) => void }) {
    const { customer, isB2B, location } = selection
    const [menuOpen, setMenuOpen] = useState(false)
    const [editContactOpen, setEditContactOpen] = useState(false)
    const [editShippingOpen, setEditShippingOpen] = useState(false)
    const [editBillingOpen, setEditBillingOpen] = useState(false)
    const [contactOverride, setContactOverride] = useState<{ email: string; phone: string } | null>(null)
    const [shippingOverride, setShippingOverride] = useState<any>(null)
    const [billingOverride, setBillingOverride] = useState<any>(null)

    // Use refs to avoid stale closures in save handlers
    const overridesRef = useRef({ contact: contactOverride, shipping: shippingOverride, billing: billingOverride })
    overridesRef.current = { contact: contactOverride, shipping: shippingOverride, billing: billingOverride }

    const handleContactSave = (e: string, p: string) => {
        const override = { email: e, phone: p }
        setContactOverride(override)
        onAddressChange?.({ ...overridesRef.current, contact: override })
    }
    const handleShippingSave = (addr: any) => {
        setShippingOverride(addr)
        onAddressChange?.({ ...overridesRef.current, shipping: addr })
    }
    const handleBillingSave = (addr: any) => {
        setBillingOverride(addr)
        onAddressChange?.({ ...overridesRef.current, billing: addr })
    }
    const customerId = customer.id.split('/').pop()

    const shippingAddress = shippingOverride || (isB2B && location ? location.shippingAddress : customer.defaultAddress)
    const billingAddress = billingOverride || (isB2B && location ? location.billingAddress : null)
    const companyProfile = isB2B ? customer.companyContactProfiles?.[0] : null
    const isTaxExempt = location?.taxExemptions?.length ? true : false
    const email = contactOverride?.email || customer.email || ''
    const phone = contactOverride?.phone || customer.phone || ''

    const toAddressForm = (addr: any) => ({
        firstName: addr?.firstName || customer.firstName || '',
        lastName: addr?.lastName || customer.lastName || '',
        company: addr?.companyName || addr?.company || companyProfile?.company?.name || '',
        address1: addr?.address1 || '',
        address2: addr?.address2 || '',
        city: addr?.city || '',
        zip: addr?.zip || '',
        countryCode: addr?.countryCode || 'DE',
        phone: addr?.phone || phone,
    })

    return (
        <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">Customer</Text>
                <Popover
                    active={menuOpen}
                    activator={
                        <Button icon={MenuHorizontalIcon} variant="plain" onClick={() => setMenuOpen(v => !v)} accessibilityLabel="Edit customer" />
                    }
                    onClose={() => setMenuOpen(false)}
                >
                    <ActionList
                        items={[
                            { content: 'Edit contact information', onAction: () => { setMenuOpen(false); setEditContactOpen(true) } },
                            { content: 'Edit shipping address', onAction: () => { setMenuOpen(false); setEditShippingOpen(true) } },
                            { content: 'Edit billing address', onAction: () => { setMenuOpen(false); setEditBillingOpen(true) } },
                            { content: 'Remove customer', destructive: true, onAction: () => { setMenuOpen(false); onRemove() } },
                        ]}
                    />
                </Popover>
            </InlineStack>

            <EditContactModal
                open={editContactOpen}
                email={email}
                phone={phone}
                customerId={customerId!}
                onSave={handleContactSave}
                onClose={() => setEditContactOpen(false)}
            />
            <EditAddressModal
                open={editShippingOpen}
                title="Edit shipping address"
                address={toAddressForm(shippingAddress)}
                onSave={handleShippingSave}
                onClose={() => setEditShippingOpen(false)}
            />
            <EditAddressModal
                open={editBillingOpen}
                title="Edit billing address"
                address={toAddressForm(billingAddress || shippingAddress)}
                onSave={handleBillingSave}
                onClose={() => setEditBillingOpen(false)}
            />

            <BlockStack gap="100">
                <Link url={adminUrl(`customers/${customerId}`)} target="_blank">
                    {[customer.firstName, customer.lastName].filter(Boolean).join(' ')}
                </Link>
                {customer.numberOfOrders && (
                    <Link url={adminUrl(`orders?customer_id=${customerId}`)} target="_blank">
                        {customer.numberOfOrders} {customer.numberOfOrders === '1' ? 'order' : 'orders'}
                    </Link>
                )}
            </BlockStack>

            <Divider />
            <Text as="p" variant="headingSm" fontWeight="bold">Contact information</Text>
            {email && (
                <Text as="p" tone="subdued">{email}</Text>
            )}
            {phone && <Text as="p" tone="subdued">{phone}</Text>}

            {companyProfile && (
                <>
                    <Divider />
                    <Text as="p" variant="headingSm" fontWeight="bold">Company</Text>
                    <Link url={adminUrl(`companies/${companyProfile.company.id.split('/').pop()}`)} target="_blank">
                        {companyProfile.company.name}
                    </Link>
                    {isTaxExempt && (
                        <Text as="p" tone="subdued" variant="bodySm">Company location is tax-exempt</Text>
                    )}
                </>
            )}

            <Divider />
            <AddressBlock label="Shipping address" address={shippingAddress} />

            {billingAddress ? (
                <>
                    <Divider />
                    <AddressBlock label="Billing address" address={billingAddress} />
                </>
            ) : (
                <>
                    <Divider />
                    <Text as="p" variant="headingSm" fontWeight="bold">Billing address</Text>
                    <Text as="p" tone="subdued" variant="bodySm">Same as shipping address</Text>
                </>
            )}
        </BlockStack>
    )
}

export function CustomerPicker({ selection, onSelect, onRemove, onAddressChange }: Props) {
    const [query, setQuery] = useState('')
    const [allCustomers, setAllCustomers] = useState<CustomerSearchResult[]>([])
    const [filtered, setFiltered] = useState<CustomerSearchResult[]>([])
    const [loading, setLoading] = useState(false)
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [state, setState] = useState<PickerState>('search')
    const [submenuCustomer, setSubmenuCustomer] = useState<CustomerSearchResult | null>(null)
    const timerRef = useRef<any>(null)

    useEffect(() => {
        setLoading(true)
        searchCustomers()
            .then(c => { setAllCustomers(c); setFiltered(c) })
            .catch(() => {})
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => {
        return () => {
            clearTimeout(timerRef.current)
        }
    }, [])

    const filterCustomers = useCallback((q: string) => {
        if (!q.trim()) { setFiltered(allCustomers); return }
        const lower = q.toLowerCase()
        const local = allCustomers.filter(c =>
            [c.firstName, c.lastName, c.email, c.companyContactProfiles?.[0]?.company?.name]
                .filter(Boolean).join(' ').toLowerCase().includes(lower)
        )
        if (local.length > 0) {
            setFiltered(local)
        } else {
            setLoading(true)
            searchCustomers(q)
                .then(setFiltered)
                .catch(() => setFiltered([]))
                .finally(() => setLoading(false))
        }
    }, [allCustomers])

    const handleQueryChange = useCallback((value: string) => {
        setQuery(value)
        setState('search')
        clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => filterCustomers(value), 200)
    }, [filterCustomers])

    const handleFocus = useCallback(() => {
        setDropdownOpen(true)
        setState('search')
        setFiltered(allCustomers)
    }, [allCustomers])

    const handleBlur = useCallback(() => {
        setTimeout(() => setDropdownOpen(false), 250)
    }, [])

    const handleCustomerClick = useCallback((customer: CustomerSearchResult) => {
        const hasCompany = customer.companyContactProfiles?.length > 0
        if (hasCompany) {
            setState('submenu')
            setSubmenuCustomer(customer)
        } else {
            onSelect({ customer, isB2B: false, location: null })
            setDropdownOpen(false)
            setQuery('')
        }
    }, [onSelect])

    const handleCompanyLocationSelect = useCallback((location: CompanyLocation) => {
        if (submenuCustomer) {
            onSelect({ customer: submenuCustomer, isB2B: true, location })
            setDropdownOpen(false)
            setQuery('')
            setState('search')
        }
    }, [submenuCustomer, onSelect])

    const handlePersonalSelect = useCallback(() => {
        if (submenuCustomer) {
            onSelect({ customer: submenuCustomer, isB2B: false, location: null })
            setDropdownOpen(false)
            setQuery('')
            setState('search')
        }
    }, [submenuCustomer, onSelect])

    if (selection) {
        return <SelectedCustomerView selection={selection} onRemove={onRemove} onAddressChange={onAddressChange} />
    }

    return (
        <BlockStack gap="200">
            <Text as="h2" variant="headingMd">Customer</Text>
            <TextField
                label="Search customers"
                labelHidden
                placeholder="Search or create a customer"
                value={query}
                onChange={handleQueryChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                autoComplete="off"
                prefix={<Icon source={SearchIcon} />}
                loading={loading}
            />
            {dropdownOpen && (
                <Card padding="0">
                    <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                        {state === 'search' && (
                            <>
                                {filtered.map(c => {
                                    const hasCompany = c.companyContactProfiles?.length > 0
                                    return (
                                        <div
                                            key={c.id}
                                            style={{
                                                padding: '10px 14px', cursor: 'pointer',
                                                borderBottom: '1px solid #f6f6f7',
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            }}
                                            onMouseDown={(e) => { e.preventDefault(); handleCustomerClick(c) }}
                                        >
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: 13 }}>
                                                    {[c.firstName, c.lastName].filter(Boolean).join(' ') || 'No name'}
                                                </div>
                                                <div style={{ color: '#6d7175', fontSize: 12 }}>
                                                    {c.email || ''}
                                                </div>
                                            </div>
                                            {hasCompany && (
                                                <span style={{ color: '#8c9196', fontSize: 18 }}>›</span>
                                            )}
                                        </div>
                                    )
                                })}
                                {filtered.length === 0 && !loading && (
                                    <div style={{ padding: '12px 14px', color: '#6d7175', fontSize: 13 }}>
                                        No customers found
                                    </div>
                                )}
                            </>
                        )}

                        {state === 'submenu' && submenuCustomer && (
                            <>
                                <div
                                    style={{
                                        padding: '10px 14px', cursor: 'pointer',
                                        background: '#f6f6f7', borderBottom: '1px solid #e1e3e5',
                                        display: 'flex', alignItems: 'center', gap: 8,
                                    }}
                                    onMouseDown={(e) => { e.preventDefault(); setState('search'); setSubmenuCustomer(null) }}
                                >
                                    <span style={{ fontSize: 16, fontWeight: 600 }}>‹</span>
                                    <span style={{ fontWeight: 600, fontSize: 13 }}>
                                        {[submenuCustomer.firstName, submenuCustomer.lastName].filter(Boolean).join(' ')}
                                    </span>
                                </div>
                                {submenuCustomer.companyContactProfiles?.map(cp =>
                                    cp.company.locations.edges.map(({ node: loc }) => (
                                        <div
                                            key={loc.id}
                                            style={{
                                                padding: '10px 14px', cursor: 'pointer',
                                                borderBottom: '1px solid #f6f6f7',
                                            }}
                                            onMouseDown={(e) => { e.preventDefault(); handleCompanyLocationSelect(loc) }}
                                        >
                                            <div style={{ fontWeight: 600, fontSize: 13 }}>{cp.company.name}</div>
                                            <div style={{ color: '#6d7175', fontSize: 12 }}>
                                                {loc.shippingAddress?.address1 || loc.name || ''}
                                            </div>
                                        </div>
                                    ))
                                )}
                                <div
                                    style={{
                                        padding: '10px 14px', cursor: 'pointer',
                                        borderBottom: '1px solid #f6f6f7',
                                    }}
                                    onMouseDown={(e) => { e.preventDefault(); handlePersonalSelect() }}
                                >
                                    <div style={{ fontSize: 13 }}>Personal order</div>
                                </div>
                            </>
                        )}
                    </div>
                </Card>
            )}
        </BlockStack>
    )
}
